import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, CheckCircle, Clock, Edit2, Eye, Loader2, Plus, Search, Trash2, Truck, X, ChevronDown, ChevronUp, Mail, FileText, ChevronLeft, ChevronRight, Copy, SkipForward, Upload, DollarSign, Download, User, Gift, Check, Timer, Calendar, Star, Mic, StopCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";
import confetti from "canvas-confetti";
import { ConditionPhotos } from "./ConditionPhotos";
import { PhotoLightbox } from "./PhotoLightbox";
import { CompletedJobsArchive } from "./CompletedJobsArchive";
import { VoiceButton } from "./VoiceButton";

const API = `/api`;

const SERVICE_TYPES = [
  "Full Respray",
  "Panel Repair",
  "Bumper Repair",
  "Scratch Repair",
  "Dent Removal",
  "Custom Paint",
  "Clear Coat",
  "Other"
];

const celebrate = () => {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6']
  });
};

const STATUS_COLORS = {
  new: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  contacted: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
  completed: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
  reschedule: "bg-amber-500/20 text-amber-400 border-amber-500/30",
};


function JobTrackerAdmin({ jobs, onUpdate, onUpdateSingle, onFullRefresh, showCreateJob, setShowCreateJob }) {
  const [updatingStage, setUpdatingStage] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [newJob, setNewJob] = useState({ customer_name: "", customer_email: "", customer_phone: "", vehicle: "", service: "Full Respray", notes: "", referral_code: "", quoted_price: "" });
  const [newJobPhotos, setNewJobPhotos] = useState([]); // Pre-work photos for new job
  const [creating, setCreating] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState("");
  // Store pending notes per stage (so they don't get lost when uploading photos)
  const [pendingNotes, setPendingNotes] = useState({}); // { "jobId-stageId": "notes text" }
  // Photo staging state - for preview before upload
  const [stagedPhotos, setStagedPhotos] = useState({}); // { "jobId-stageId": [{ file, preview }] }
  // Price editing state
  const [editingPrices, setEditingPrices] = useState(null);
  const [priceForm, setPriceForm] = useState({ quoted: "", final: "" });
  // Timer state
  const [activeTimers, setActiveTimers] = useState({}); // { job_id: [timer1, timer2, ...] }
  const [timerLoading, setTimerLoading] = useState(null);
  const [clockOutModal, setClockOutModal] = useState(null); // { jobId, username }
  const [clockOutNotes, setClockOutNotes] = useState("");
  // Email resend state
  const [resendingEmail, setResendingEmail] = useState(null);
  // Referral validation state
  const [referralValidating, setReferralValidating] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  // Xero sync state
  const [xeroSyncing, setXeroSyncing] = useState(null);
  // Manual timer editing state
  const [timerModal, setTimerModal] = useState(null); // { jobId, jobVehicle }
  const [jobTimerHistory, setJobTimerHistory] = useState([]);
  const [timerHistoryLoading, setTimerHistoryLoading] = useState(false);
  const [addingManualTimer, setAddingManualTimer] = useState(false);
  const [manualTimerForm, setManualTimerForm] = useState({ user: "", date: "", start_time: "", end_time: "", notes: "" });
  const [editingTimerId, setEditingTimerId] = useState(null);
  const [editTimerForm, setEditTimerForm] = useState({ clock_in: "", clock_out: "", notes: "" });
  // AI Tracker Description state
  const [aiGenerating, setAiGenerating] = useState(null); // "jobId-stageId"
  const [aiVoiceInput, setAiVoiceInput] = useState("");
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const recognitionRef = useRef(null);

  // Fetch active timers
  useEffect(() => {
    const fetchActiveTimers = async () => {
      try {
        const res = await api.get(`${API}/admin/active-timers`);
        const timersMap = {};
        res.data.active_timers.forEach(t => {
          if (!timersMap[t.job_id]) timersMap[t.job_id] = [];
          timersMap[t.job_id].push(t);
        });
        setActiveTimers(timersMap);
      } catch (err) {
        console.log("Could not fetch active timers");
      }
    };
    fetchActiveTimers();
    // Refresh every 30 seconds
    const interval = setInterval(fetchActiveTimers, 30000);
    return () => clearInterval(interval);
  }, []);

  const refreshTimers = async () => {
    const res = await api.get(`${API}/admin/active-timers`);
    const timersMap = {};
    res.data.active_timers.forEach(t => {
      if (!timersMap[t.job_id]) timersMap[t.job_id] = [];
      timersMap[t.job_id].push(t);
    });
    setActiveTimers(timersMap);
  };

  const clockIn = async (jobId, username) => {
    setTimerLoading(jobId);
    try {
      await api.post(`${API}/admin/jobs/${jobId}/clock-in`, { user: username });
      toast.success("Clocked in!");
      await refreshTimers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clock in");
    } finally {
      setTimerLoading(null);
    }
  };

  const clockOut = async (jobId, username, notes = "") => {
    setTimerLoading(jobId);
    try {
      const res = await api.post(`${API}/admin/jobs/${jobId}/clock-out`, { user: username, notes });
      toast.success(`Clocked out! ${res.data.duration_minutes} mins recorded`);
      await refreshTimers();
      onUpdate(); // Refresh jobs to get updated total time
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clock out");
    } finally {
      setTimerLoading(null);
      setClockOutModal(null);
      setClockOutNotes("");
    }
  };

  const handleClockOutClick = (jobId, username) => {
    setClockOutModal({ jobId, username });
    setClockOutNotes("");
  };

  const submitClockOut = () => {
    if (clockOutModal) {
      clockOut(clockOutModal.jobId, clockOutModal.username, clockOutNotes);
    }
  };

  // Manual timer functions
  const openTimerModal = async (job) => {
    setTimerModal({ jobId: job.id, jobVehicle: job.vehicle, jobCustomer: job.customer_name });
    setTimerHistoryLoading(true);
    setAddingManualTimer(false);
    setEditingTimerId(null);
    setManualTimerForm({ user: "", date: "", start_time: "", end_time: "", notes: "" });
    
    try {
      const res = await api.get(`${API}/admin/jobs/${job.id}/timers`);
      setJobTimerHistory(res.data.timers || []);
    } catch (err) {
      console.error("Failed to load timer history:", err);
      setJobTimerHistory([]);
    } finally {
      setTimerHistoryLoading(false);
    }
  };

  const closeTimerModal = () => {
    setTimerModal(null);
    setJobTimerHistory([]);
    setAddingManualTimer(false);
    setEditingTimerId(null);
  };

  const addManualTimer = async () => {
    if (!manualTimerForm.user || !manualTimerForm.date || !manualTimerForm.start_time || !manualTimerForm.end_time) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Construct ISO datetime strings
    const clock_in = `${manualTimerForm.date}T${manualTimerForm.start_time}:00`;
    const clock_out = `${manualTimerForm.date}T${manualTimerForm.end_time}:00`;
    
    setTimerLoading("manual-add");
    try {
      const res = await api.post(`${API}/admin/jobs/${timerModal.jobId}/timers/manual`, {
        user: manualTimerForm.user,
        clock_in,
        clock_out,
        notes: manualTimerForm.notes || ""
      });
      toast.success(`Manual entry added: ${res.data.duration_minutes} mins`);
      setManualTimerForm({ user: "", date: "", start_time: "", end_time: "", notes: "" });
      setAddingManualTimer(false);
      
      // Refresh timer history
      const timersRes = await api.get(`${API}/admin/jobs/${timerModal.jobId}/timers`);
      setJobTimerHistory(timersRes.data.timers || []);
      onUpdate(); // Refresh jobs to get updated total time
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add manual timer");
    } finally {
      setTimerLoading(null);
    }
  };

  const startEditTimer = (timer) => {
    setEditingTimerId(timer.id);
    // Parse existing times for the form
    const clockIn = new Date(timer.clock_in);
    const clockOut = timer.clock_out ? new Date(timer.clock_out) : null;
    setEditTimerForm({
      clock_in: clockIn.toISOString().slice(0, 16), // YYYY-MM-DDTHH:MM format
      clock_out: clockOut ? clockOut.toISOString().slice(0, 16) : "",
      notes: timer.notes || ""
    });
  };

  const saveEditTimer = async (timerId) => {
    setTimerLoading(`edit-${timerId}`);
    try {
      await api.put(`${API}/admin/timers/${timerId}`, {
        clock_in: editTimerForm.clock_in ? new Date(editTimerForm.clock_in).toISOString() : undefined,
        clock_out: editTimerForm.clock_out ? new Date(editTimerForm.clock_out).toISOString() : undefined,
        notes: editTimerForm.notes
      });
      toast.success("Timer updated");
      setEditingTimerId(null);
      
      // Refresh timer history
      const timersRes = await api.get(`${API}/admin/jobs/${timerModal.jobId}/timers`);
      setJobTimerHistory(timersRes.data.timers || []);
      onUpdate(); // Refresh jobs to get updated total time
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update timer");
    } finally {
      setTimerLoading(null);
    }
  };

  const deleteTimer = async (timerId) => {
    if (!window.confirm("Delete this timer entry?")) return;
    
    setTimerLoading(`delete-${timerId}`);
    try {
      await api.delete(`${API}/admin/timers/${timerId}`);
      toast.success("Timer deleted");
      
      // Refresh timer history
      const timersRes = await api.get(`${API}/admin/jobs/${timerModal.jobId}/timers`);
      setJobTimerHistory(timersRes.data.timers || []);
      onUpdate(); // Refresh jobs to get updated total time
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete timer");
    } finally {
      setTimerLoading(null);
    }
  };

  // Filter jobs by search
  const filterJobs = (jobList) => {
    if (!searchQuery) return jobList;
    const q = searchQuery.toLowerCase();
    return jobList.filter(j => 
      j.customer_name?.toLowerCase().includes(q) ||
      j.vehicle?.toLowerCase().includes(q) ||
      j.customer_phone?.includes(q) ||
      j.tracking_code?.toLowerCase().includes(q)
    );
  };

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      setReferralInfo(null);
      return;
    }
    
    setReferralValidating(true);
    try {
      const res = await api.get(`${API}/referral/validate/${code}`);
      if (res.data.valid) {
        setReferralValid(true);
        setReferralInfo(res.data);
        toast.success(`Referral code valid! Referred by ${res.data.referrer_name}`);
      } else {
        setReferralValid(false);
        setReferralInfo(null);
      }
    } catch (err) {
      setReferralValid(false);
      setReferralInfo(null);
    } finally {
      setReferralValidating(false);
    }
  };

  const createJob = async (e) => {
    e.preventDefault();
    if (!newJob.customer_name || !newJob.customer_phone || !newJob.vehicle) {
      toast.error("Name, phone and vehicle are required");
      return;
    }
    setCreating(true);
    try {
      // Include quoted price if provided
      const jobData = {
        ...newJob,
        quoted_price: newJob.quoted_price ? parseFloat(newJob.quoted_price) : null
      };
      
      const res = await api.post(`${API}/admin/jobs`, jobData);
      const jobId = res.data.id;
      
      // Upload pre-work photos if any
      if (newJobPhotos.length > 0) {
        try {
          const photoBase64s = newJobPhotos.map(p => p.base64);
          await api.patch(`${API}/admin/jobs/${jobId}/condition-photos`, {
            photo_type: "pre_work",
            photos: photoBase64s
          });
        } catch (photoErr) {
          console.error("Failed to upload pre-work photos:", photoErr);
          toast.error("Job created but some photos failed to upload");
        }
      }
      
      // If referral code is valid, apply it to the job
      if (referralValid && newJob.referral_code) {
        try {
          await api.post(`${API}/admin/referrals/use`, {
            referral_code: newJob.referral_code.toUpperCase(),
            job_id: jobId
          });
          toast.success(`Referral code applied! ${referralInfo?.referrer_name} will be notified.`);
        } catch (refErr) {
          console.error("Failed to apply referral code:", refErr);
        }
      }
      
      toast.success("Job created! Tracking code sent to customer.");
      setShowCreateJob(false);
      setNewJob({ customer_name: "", customer_email: "", customer_phone: "", vehicle: "", service: "Full Respray", notes: "", referral_code: "", quoted_price: "" });
      setNewJobPhotos([]);
      setReferralValid(null);
      setReferralInfo(null);
      onUpdate();
    } catch (err) {
      toast.error("Failed to create job");
    } finally {
      setCreating(false);
    }
  };

  // Handle pre-work photo upload for new job
  const handleNewJobPhotoUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const remaining = 5 - newJobPhotos.length;
    if (remaining <= 0) {
      toast.error("Maximum 5 pre-work photos");
      return;
    }
    
    const fileArray = Array.from(files).slice(0, remaining);
    
    for (const file of fileArray) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      const preview = URL.createObjectURL(file);
      setNewJobPhotos(prev => [...prev, { file, preview, base64 }]);
    }
    
    toast.success(`${fileArray.length} photo(s) ready`);
  };

  const removeNewJobPhoto = (index) => {
    setNewJobPhotos(prev => {
      const photo = prev[index];
      if (photo?.preview) URL.revokeObjectURL(photo.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateStage = async (jobId, stageId, status, photos = null) => {
    setUpdatingStage(`${jobId}-${stageId}`);
    try {
      // Get the logged-in user from localStorage
      const loggedInUser = localStorage.getItem("dashboard_username") || localStorage.getItem("username");
      await api.put(`${API}/admin/jobs/${jobId}/stage`, { 
        stage_id: stageId, 
        status, 
        photos,
        user: loggedInUser 
      });
      toast.success(`Stage ${status === "skipped" ? "skipped" : "completed"}${photos ? ` with ${photos.length} photo(s)` : ""}`);
      
      // Trigger confetti when "Ready for Pickup" stage is completed
      if (stageId === "ready" && status === "completed") {
        celebrate();
        toast.success("Job Complete! Vehicle ready for pickup!", { duration: 5000 });
        // Full refresh needed when job completes to update stats
        if (onFullRefresh) onFullRefresh();
      } else {
        // Light refresh for stage updates - just refresh jobs list, not stats
        onUpdate();
      }
    } catch (err) {
      toast.error("Failed to update stage");
    } finally {
      setUpdatingStage(null);
    }
  };

  const saveNotes = async (jobId, stageId) => {
    try {
      await api.patch(`${API}/admin/jobs/${jobId}/stage/${stageId}/notes`, { notes: notesText });
      toast.success("Notes saved");
      setEditingNotes(null);
      onUpdate();
    } catch (err) {
      toast.error("Failed to save notes");
    }
  };

  const resendEmail = async (jobId, emailType) => {
    // Confirmation popup with email description
    const emailDescriptions = {
      tracking: "Send Tracking Code email - Contains the tracking link for the customer to view their job progress",
      stage_update: "Send Stage Update email - Notifies the customer about the current stage completion with photos",
      ready: "Send Ready for Pickup email - Notifies the customer their vehicle is ready for collection",
      aftercare: "Send Aftercare Guide email - Contains the paint care dos and don'ts guide"
    };
    
    if (!window.confirm(`${emailDescriptions[emailType]}\n\nSend this email?`)) return;
    
    setResendingEmail(`${jobId}-${emailType}`);
    try {
      const res = await api.post(`${API}/admin/jobs/${jobId}/resend-email?email_type=${emailType}`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setResendingEmail(null);
    }
  };

  const openAdminPreview = (trackingCode) => {
    window.open(`/track/${trackingCode}?admin_preview=true`, '_blank');
  };

  const deletePhoto = async (jobId, photoType, photoIndex, stageId = null) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.patch(`${API}/admin/jobs/${jobId}/delete-photo`, {
        photo_type: photoType,
        photo_index: photoIndex,
        stage_id: stageId
      });
      toast.success("Photo deleted");
      onUpdate();
    } catch (err) {
      toast.error("Failed to delete photo");
    }
  };

  const handlePhotoUpload = async (jobId, stageId, files) => {
    if (!files || files.length === 0) return;
    
    const key = `${jobId}-${stageId}`;
    const currentStaged = stagedPhotos[key] || [];
    const remaining = 10 - currentStaged.length;
    
    if (remaining <= 0) {
      toast.error("Maximum 10 photos per stage");
      return;
    }
    
    const fileArray = Array.from(files).slice(0, remaining);
    const newStaged = [];
    
    for (const file of fileArray) {
      const preview = URL.createObjectURL(file);
      newStaged.push({ file, preview });
    }
    
    setStagedPhotos(prev => ({
      ...prev,
      [key]: [...currentStaged, ...newStaged]
    }));
    
    // Auto-open notes editor when first photos are staged (helps prevent notes being forgotten)
    if (currentStaged.length === 0 && editingNotes !== key) {
      // Find existing notes for this stage from jobs data
      const job = jobs.jobs?.find(j => j.id === jobId);
      const existingNotes = job?.stages?.[stageId]?.notes || pendingNotes[key] || "";
      setEditingNotes(key);
      setNotesText(existingNotes);
    }
    
    toast.success(`${newStaged.length} photo(s) ready - add notes below, then click "Upload & Complete"`);
  };

  const removeStagedPhoto = (jobId, stageId, index) => {
    const key = `${jobId}-${stageId}`;
    setStagedPhotos(prev => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index)
    }));
  };

  const uploadStagedPhotos = async (jobId, stageId) => {
    const key = `${jobId}-${stageId}`;
    const staged = stagedPhotos[key];
    
    if (!staged || staged.length === 0) {
      toast.error("No photos to upload");
      return;
    }
    
    setUpdatingStage(`${jobId}-${stageId}`);
    
    try {
      // Capture notes from multiple sources to prevent data loss:
      // 1. Check pendingNotes (stored when user types)
      // 2. Check notesText if this stage is being edited
      // 3. Also check the DOM directly as a last resort backup
      let notesToSave = pendingNotes[key];
      if (!notesToSave && editingNotes === key) {
        notesToSave = notesText;
      }
      // Fallback: try to get value directly from textarea if it exists
      if (!notesToSave) {
        const textarea = document.querySelector(`textarea[data-notes-key="${key}"]`);
        if (textarea && textarea.value) {
          notesToSave = textarea.value;
        }
      }
      
      // Save notes first if there are any pending
      if (notesToSave && notesToSave.trim()) {
        try {
          await api.patch(`${API}/admin/jobs/${jobId}/stage/${stageId}/notes`, { notes: notesToSave });
          toast.success("Notes saved");
        } catch (noteErr) {
          console.error("Failed to save notes:", noteErr);
          // Continue with photo upload even if notes fail
        }
      }
      
      // Clear notes editing state after successful save
      setEditingNotes(null);
      setPendingNotes(prev => { const n = {...prev}; delete n[key]; return n; });
      
      // Upload photos one at a time to avoid payload size limits
      let uploadedCount = 0;
      
      for (const { file } of staged) {
        try {
          // Compress image before upload (smaller size for mobile)
          const base64 = await compressImage(file, 800, 0.6);
          
          // Upload single photo
          await api.put(`${API}/admin/jobs/${jobId}/stage`, { 
            stage_id: stageId, 
            status: uploadedCount === staged.length - 1 ? "completed" : "pending",
            photos: [base64]
          });
          uploadedCount++;
        } catch (uploadErr) {
          console.error("Single photo upload error:", uploadErr);
          if (uploadedCount === 0) {
            throw uploadErr; // Only throw if no photos uploaded at all
          }
          toast.error(`Uploaded ${uploadedCount}/${staged.length} photos`);
          break;
        }
      }
      
      // Mark stage as completed after all photos uploaded
      if (uploadedCount > 0) {
        await api.put(`${API}/admin/jobs/${jobId}/stage`, { 
          stage_id: stageId, 
          status: "completed"
        });
        toast.success(`Stage completed with ${uploadedCount} photo(s)`);
      }
      
      // Clear staged photos and revoke URLs
      staged.forEach(({ preview }) => URL.revokeObjectURL(preview));
      setStagedPhotos(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      
      onUpdate();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data?.detail || "Failed to upload photos - try fewer photos");
    } finally {
      setUpdatingStage(null);
    }
  };

  // Helper function to compress images
  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = (e) => {
        const img = new window.Image();
        img.onerror = () => reject(new Error("Failed to load image"));
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Scale down if needed
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const base64 = canvas.toDataURL('image/jpeg', quality);
          resolve(base64);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Voice recording for AI description input
  const startVoiceRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Voice input not supported. Try Chrome or Edge.");
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';
    
    recognition.onstart = () => {
      setIsRecordingVoice(true);
      toast.info("Listening... Describe the work done");
    };
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' ';
        }
      }
      if (finalTranscript) {
        setAiVoiceInput(prev => prev + finalTranscript);
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        toast.error(`Voice error: ${event.error}`);
      }
      setIsRecordingVoice(false);
    };
    
    recognition.onend = () => setIsRecordingVoice(false);
    
    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopVoiceRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecordingVoice(false);
      toast.success("Voice captured!");
    }
  };

  // Generate AI description for tracker
  const generateAIDescription = async (job, stageId, stageName, photoUrls) => {
    const key = `${job.id}-${stageId}`;
    setAiGenerating(key);
    
    try {
      const res = await api.post(`${API}/admin/jobs/generate-tracker-description`, {
        stage_id: stageId,
        stage_name: stageName,
        vehicle: job.vehicle,
        text_input: aiVoiceInput || null,
        photo_urls: photoUrls?.length > 0 ? photoUrls : null
      });
      
      const description = res.data.description;
      
      // Set the description in the notes textarea
      setNotesText(description);
      setEditingNotes(key);
      setPendingNotes(prev => ({ ...prev, [key]: description }));
      
      toast.success("AI description generated! Review and save.");
      setAiVoiceInput(""); // Clear voice input after use
      
    } catch (err) {
      console.error("AI generation failed:", err);
      toast.error(err.response?.data?.detail || "AI generation failed");
    } finally {
      setAiGenerating(null);
    }
  };

  const addMorePhotos = async (jobId, stageId, files, currentCount) => {
    if (!files || files.length === 0) return;
    
    const remaining = 10 - currentCount;
    if (remaining <= 0) {
      toast.error("Maximum 10 photos per stage");
      return;
    }
    
    const fileArray = Array.from(files).slice(0, remaining);
    
    setUpdatingStage(`${jobId}-${stageId}-add`);
    try {
      // Upload photos one at a time with compression to avoid payload size limits
      let uploadedCount = 0;
      
      for (const file of fileArray) {
        try {
          // Compress image before upload
          const base64 = await compressImage(file, 800, 0.6);
          
          // Upload single photo
          await api.put(`${API}/admin/jobs/${jobId}/stage`, { 
            stage_id: stageId, 
            status: "completed", 
            photos: [base64] 
          });
          uploadedCount++;
        } catch (uploadErr) {
          console.error("Single photo upload error:", uploadErr);
          if (uploadedCount === 0) {
            throw uploadErr;
          }
          toast.error(`Uploaded ${uploadedCount}/${fileArray.length} photos`);
          break;
        }
      }
      
      if (uploadedCount > 0) {
        toast.success(`Added ${uploadedCount} photo(s)`);
        onUpdate();
      }
    } catch (err) {
      console.error("Photo upload error:", err);
      toast.error(err.response?.data?.detail || "Failed to add photos - try again");
    } finally {
      setUpdatingStage(null);
    }
  };

  const deleteJob = async (jobId, vehicle) => {
    if (!window.confirm(`Delete job for ${vehicle}? This cannot be undone.`)) return;
    try {
      await api.delete(`${API}/admin/jobs/${jobId}`);
      toast.success("Job deleted");
      onUpdate();
    } catch (err) {
      toast.error("Failed to delete job");
    }
  };

  const savePrices = async (jobId) => {
    try {
      await api.patch(`${API}/admin/jobs/${jobId}/prices`, {
        quoted_price: priceForm.quoted ? parseFloat(priceForm.quoted) : null,
        final_price: priceForm.final ? parseFloat(priceForm.final) : null
      });
      toast.success("Prices updated");
      setEditingPrices(null);
      onUpdate();
    } catch (err) {
      toast.error("Failed to update prices");
    }
  };

  const startEditingPrices = (job) => {
    setEditingPrices(job.id);
    setPriceForm({
      quoted: job.quoted_price?.toString() || "",
      final: job.final_price?.toString() || ""
    });
  };

  const copyTrackingCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Tracking code copied!");
  };

  const syncToXero = async (jobId) => {
    setXeroSyncing(jobId);
    try {
      const res = await api.post(`${API}/xero/sync-job/${jobId}`);
      if (res.data.already_synced) {
        toast.info(`Already synced - Invoice #${res.data.invoice_number}`);
      } else {
        toast.success(`Xero invoice created: #${res.data.invoice_number}`);
      }
      onUpdate();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to sync to Xero";
      if (msg.includes("Not connected")) {
        toast.error("Connect Xero first (see Calendar tab)");
      } else if (msg.includes("no price")) {
        toast.error("Set a price before syncing to Xero");
      } else {
        toast.error(msg);
      }
    } finally {
      setXeroSyncing(null);
    }
  };

  // Helper to get photos array (handles old single photo format)
  const getStagePhotos = (stageData) => {
    if (!stageData) return [];
    if (stageData.photos && Array.isArray(stageData.photos)) return stageData.photos;
    if (stageData.photo) return [stageData.photo];
    return [];
  };

  const inProgressJobs = filterJobs(jobs.jobs?.filter(j => j.status === "in_progress") || []);
  const completedJobs = filterJobs(jobs.jobs?.filter(j => j.status === "completed") || []);

  const inputCls = "w-full bg-transparent border-b border-white/10 text-white font-body text-sm py-2 px-0 outline-none focus:border-[#3b82f6]/50 placeholder:text-zinc-700 transition-colors";

  return (
    <div className="space-y-6" data-testid="job-tracker-admin">
      {/* Photo Lightbox */}
      {lightboxPhotos && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxPhotos(null)}
        />
      )}

      {/* Header with Create Button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg uppercase tracking-wide text-white">Job Tracker</h3>
          <p className="text-zinc-600 font-body text-sm">Track jobs in progress and update customers</p>
        </div>
        <button onClick={() => setShowCreateJob(true)} data-testid="create-job-btn"
          className="flex items-center gap-2 px-4 py-2.5 text-xs tracking-[0.1em] uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors bg-transparent cursor-pointer">
          <Plus size={14} /> New Job
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md mb-6">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search jobs by name, vehicle, phone..."
          data-testid="jobs-search-input"
          className="w-full pl-11 pr-16 py-2.5 border font-body text-sm outline-none focus:border-[#3b82f6]/50 transition-colors"
          style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
        />
        <VoiceButton
          onText={(t) => setSearchQuery((prev) => (prev ? prev + " " : "") + t.trim())}
          size={16}
          testId="jobs-search-voice-btn"
          title="Search by voice"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} className="absolute right-9 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white transition-colors bg-transparent border-none cursor-pointer">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Create Job Form */}
      {showCreateJob && (
        <div className="border border-[#22c55e]/20 p-6 mb-6" style={{ backgroundColor: 'var(--dash-card)' }}>
          <h4 className="font-heading text-base uppercase tracking-wide mb-4" style={{ color: 'var(--dash-text)' }}>Start New Job</h4>
          <form onSubmit={createJob} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input value={newJob.customer_name} onChange={(e) => setNewJob(p => ({...p, customer_name: e.target.value}))} placeholder="Customer Name *" className={inputCls} />
              <input value={newJob.customer_phone} onChange={(e) => setNewJob(p => ({...p, customer_phone: e.target.value}))} placeholder="Phone *" className={inputCls} />
            </div>
            <input value={newJob.customer_email} onChange={(e) => setNewJob(p => ({...p, customer_email: e.target.value}))} placeholder="Email (for updates)" className={inputCls} />
            <input value={newJob.vehicle} onChange={(e) => setNewJob(p => ({...p, vehicle: e.target.value}))} placeholder="Vehicle (e.g. Nissan Silvia S15) *" className={inputCls} />
            <div className="flex flex-wrap gap-2">
              {SERVICE_TYPES.map((s) => (
                <button key={s} type="button" onClick={() => setNewJob(p => ({...p, service: s}))}
                  className={`px-3 py-1.5 text-xs tracking-[0.05em] uppercase font-body border cursor-pointer transition-colors ${
                    newJob.service === s ? "bg-[#3b82f6]/20 text-[#3b82f6] border-[#3b82f6]/30" : "bg-transparent border-white/10 text-zinc-600 hover:text-zinc-400"
                  }`}>
                  {s}
                </button>
              ))}
            </div>
            
            {/* Referral Code Input */}
            <div>
              <label className="text-xs text-zinc-600 uppercase tracking-wider mb-1 block">Referral Code (optional)</label>
              <div className="relative">
                <Gift size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input 
                  value={newJob.referral_code} 
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setNewJob(p => ({...p, referral_code: value}));
                    if (value.length >= 4) {
                      setTimeout(() => validateReferralCode(value), 500);
                    } else {
                      setReferralValid(null);
                      setReferralInfo(null);
                    }
                  }} 
                  placeholder="e.g. JACK-A1B2" 
                  className={`${inputCls} pl-10 pr-10 font-mono uppercase ${
                    referralValid === true ? "border-[#22c55e]/50 focus:border-[#22c55e]" :
                    referralValid === false ? "border-red-500/50 focus:border-red-500" : ""
                  }`} 
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {referralValidating && <Loader2 size={16} className="text-zinc-500 animate-spin" />}
                  {!referralValidating && referralValid === true && <Check size={16} className="text-[#22c55e]" />}
                  {!referralValidating && referralValid === false && <X size={16} className="text-red-500" />}
                </div>
              </div>
              {referralInfo && referralValid && (
                <div className="mt-2 p-2 bg-[#22c55e]/10 border border-[#22c55e]/30">
                  <p className="text-xs text-[#22c55e]">
                    <strong>Referred by {referralInfo.referrer_name}</strong>
                    {referralInfo.referrer_vehicle && <span className="text-[#22c55e]/70"> ({referralInfo.referrer_vehicle})</span>}
                  </p>
                </div>
              )}
            </div>
            
            {/* Quoted Price */}
            <div>
              <label className="text-xs text-zinc-600 uppercase tracking-wider mb-1 block">Quoted Price (optional)</label>
              <div className="relative">
                <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input 
                  type="number"
                  value={newJob.quoted_price} 
                  onChange={(e) => setNewJob(p => ({...p, quoted_price: e.target.value}))} 
                  placeholder="0.00" 
                  className={`${inputCls} pl-10 font-mono`}
                />
              </div>
            </div>
            
            {/* Pre-Work Photos */}
            <div>
              <label className="text-xs text-zinc-600 uppercase tracking-wider mb-2 block">Pre-Work Photos (optional)</label>
              <div className="flex gap-2 flex-wrap">
                {newJobPhotos.map((photo, idx) => (
                  <div key={idx} className="relative group">
                    <div className="w-16 h-16 border border-white/10 overflow-hidden">
                      <img src={photo.preview} alt={`Pre-work ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeNewJobPhoto(idx)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full cursor-pointer border-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
                {newJobPhotos.length < 5 && (
                  <label className="w-16 h-16 border-2 border-dashed border-zinc-700 flex items-center justify-center cursor-pointer hover:border-[#3b82f6]/50 transition-colors">
                    <Camera size={20} className="text-zinc-600" />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleNewJobPhotoUpload(e.target.files)}
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-zinc-600 mt-1">{newJobPhotos.length}/5 photos - Document car condition on arrival</p>
            </div>
            
            <div className="flex gap-2">
              <button type="submit" disabled={creating} className="px-6 py-2.5 text-xs tracking-[0.1em] uppercase font-body bg-[#22c55e] text-black hover:bg-[#22c55e]/80 transition-colors cursor-pointer border-none disabled:opacity-50">
                {creating ? "Creating..." : "Create Job"}
              </button>
              <button type="button" onClick={() => { setShowCreateJob(false); setNewJobPhotos([]); }} className="px-6 py-2.5 text-xs tracking-[0.1em] uppercase font-body border border-white/10 text-zinc-500 hover:text-white transition-colors cursor-pointer bg-transparent">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* In Progress Jobs */}
      {inProgressJobs.length > 0 && (
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-body mb-3" style={{ color: 'var(--dash-text-muted)' }}>In Progress ({inProgressJobs.length})</h4>
          <div className="space-y-2">
            {inProgressJobs.map((job) => {
              // Get first available photo as thumbnail (check stages, then pre_work_photos)
              const getJobThumbnail = () => {
                // Check stage photos first
                for (const stageId of ['repairs', 'prep', 'painted', 'assembled', 'correction', 'ready']) {
                  const photos = getStagePhotos(job.stages?.[stageId]);
                  if (photos.length > 0) return photos[0];
                }
                // Fallback to pre-work photos
                if (job.pre_work_photos?.length > 0) return job.pre_work_photos[0];
                return null;
              };
              const thumbnail = getJobThumbnail();
              
              return (
              <div key={job.id} className="border" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                {/* Job Header */}
                <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 cursor-pointer" onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}>
                  <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                    {/* Thumbnail or icon */}
                    {thumbnail ? (
                      <div className="w-10 h-10 md:w-12 md:h-12 border border-[#3b82f6]/20 flex-shrink-0 overflow-hidden">
                        <img src={thumbnail} alt={job.vehicle} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0">
                        <Truck size={18} className="text-[#3b82f6]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h4 className="font-heading text-base md:text-lg uppercase tracking-wide truncate" style={{ color: 'var(--dash-text)' }}>{job.vehicle}</h4>
                      <p className="font-body text-xs md:text-sm truncate" style={{ color: 'var(--dash-text-muted)' }}>{job.customer_name} / {job.service}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:gap-4">
                    {/* Timer Controls - Per User */}
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const storedUser = JSON.parse(localStorage.getItem("vpp_user") || "{}");
                        const currentUser = storedUser.username || "Unknown";
                        const jobTimers = activeTimers[job.id] || [];
                        const myTimer = jobTimers.find(t => t.user === currentUser);
                        const otherTimers = jobTimers.filter(t => t.user !== currentUser);
                        return (
                          <>
                            {/* Show other users working */}
                            {otherTimers.length > 0 && (
                              <div className="flex items-center gap-1">
                                {otherTimers.map(t => (
                                  <span key={t.id || t.user} className="px-2 py-1 bg-[#22c55e]/10 border border-[#22c55e]/20 text-[#22c55e] text-[10px] uppercase tracking-wider font-mono">
                                    {t.user?.replace('VPP','') || '?'}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* My timer */}
                            {myTimer ? (
                              <button
                                onClick={() => handleClockOutClick(job.id, currentUser)}
                                disabled={timerLoading === job.id}
                                className="flex items-center gap-2 px-3 py-1.5 bg-[#22c55e]/20 border border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/30 transition-colors cursor-pointer animate-pulse"
                                data-testid={`timer-stop-${job.id}`}
                              >
                                {timerLoading === job.id ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
                                <span className="text-xs uppercase tracking-wider">Stop</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => clockIn(job.id, currentUser)}
                                disabled={timerLoading === job.id}
                                className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 text-zinc-400 hover:text-white hover:border-[#3b82f6]/50 transition-colors cursor-pointer"
                                data-testid={`timer-start-${job.id}`}
                              >
                                {timerLoading === job.id ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
                                <span className="text-xs uppercase tracking-wider">Clock In</span>
                              </button>
                            )}
                          </>
                        );
                      })()}
                      {job.total_labor_minutes > 0 && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); openTimerModal(job); }}
                          className="text-xs text-zinc-500 font-mono hover:text-[#3b82f6] hover:underline cursor-pointer bg-transparent border-none flex items-center gap-1"
                          title="View/Edit timer history"
                          data-testid={`timer-history-${job.id}`}
                        >
                          <Timer size={12} />
                          {Math.floor(job.total_labor_minutes / 60)}h {Math.round(job.total_labor_minutes % 60)}m
                        </button>
                      )}
                      {/* Add manual time button if no time logged yet */}
                      {!job.total_labor_minutes && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); openTimerModal(job); }}
                          className="text-xs text-zinc-600 hover:text-[#3b82f6] cursor-pointer bg-transparent border-none flex items-center gap-1"
                          title="Add manual time entry"
                          data-testid={`timer-add-${job.id}`}
                        >
                          <Plus size={10} />
                          <Timer size={12} />
                        </button>
                      )}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); copyTrackingCode(job.tracking_code); }}
                      className="flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-white/5 border border-white/10 text-zinc-400 hover:text-white transition-colors cursor-pointer">
                      <span className="font-mono text-xs sm:text-sm tracking-wider">{job.tracking_code}</span>
                      <Copy size={12} />
                    </button>
                    {/* Customer viewed indicator */}
                    {job.last_viewed_at && (
                      <span className="hidden sm:flex items-center gap-1 text-xs text-[#22c55e]" title={`Last viewed: ${new Date(job.last_viewed_at).toLocaleString()}`}>
                        <Eye size={12} />
                        <span className="text-zinc-500">
                          {(() => {
                            const diff = Date.now() - new Date(job.last_viewed_at).getTime();
                            const mins = Math.floor(diff / 60000);
                            const hours = Math.floor(diff / 3600000);
                            const days = Math.floor(diff / 86400000);
                            if (mins < 60) return `${mins}m ago`;
                            if (hours < 24) return `${hours}h ago`;
                            return `${days}d ago`;
                          })()}
                        </span>
                      </span>
                    )}
                    <span className="hidden sm:inline text-zinc-600 text-xs font-mono">
                      {Object.values(job.stages).filter(s => s.status === "completed" || s.status === "skipped").length}/{jobs.stages?.length || 6}
                    </span>
                  </div>
                </div>

                {/* Expanded Stages */}
                {expandedJob === job.id && (
                  <div className="border-t border-white/5 p-3 md:p-5 space-y-3">
                    {jobs.stages?.map((stage) => {
                      const stageData = job.stages[stage.id];
                      const isCompleted = stageData?.status === "completed";
                      const isSkipped = stageData?.status === "skipped";
                      const isPending = !isCompleted && !isSkipped;
                      const isCurrent = job.current_stage === stage.id;
                      const photos = getStagePhotos(stageData);
                      const hasPhotos = photos.length > 0;
                      const canAddMore = isCompleted && photos.length < 10;

                      return (
                        <div key={stage.id} className={`p-3 ${isCurrent ? 'bg-[#3b82f6]/10 border border-[#3b82f6]/20' : 'bg-white/5'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {isCompleted ? <CheckCircle size={18} className="text-[#22c55e]" /> :
                               isSkipped ? <SkipForward size={18} className="text-zinc-600" /> :
                               <Clock size={18} className={isCurrent ? "text-[#3b82f6]" : "text-zinc-700"} />}
                              <span className={`font-body text-sm ${isCompleted ? 'text-[#22c55e]' : isSkipped ? 'text-zinc-600 line-through' : isCurrent ? 'text-white' : 'text-zinc-600'}`}>
                                {stage.name}
                              </span>
                              {hasPhotos && (
                                <span className="flex items-center gap-1 text-[#3b82f6] text-xs">
                                  <Camera size={12} /> {photos.length}/10
                                </span>
                              )}
                            </div>
                            {isPending && (
                              <div className="flex gap-2 items-center">
                                {/* Photo select button - allows library selection on mobile */}
                                <label className="px-3 py-1 text-xs uppercase font-body border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors cursor-pointer flex items-center gap-1">
                                  <Upload size={12} /> + Photos
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => handlePhotoUpload(job.id, stage.id, e.target.files)}
                                  />
                                </label>
                                {/* Show Upload & Complete button if photos are staged */}
                                {stagedPhotos[`${job.id}-${stage.id}`]?.length > 0 ? (
                                  <button
                                    onClick={() => uploadStagedPhotos(job.id, stage.id)}
                                    disabled={updatingStage === `${job.id}-${stage.id}`}
                                    className="px-3 py-1 text-xs uppercase font-body bg-[#22c55e] text-white border-none hover:bg-[#22c55e]/80 transition-colors cursor-pointer disabled:opacity-50"
                                  >
                                    {updatingStage === `${job.id}-${stage.id}` ? "Uploading..." : `Upload & Complete (${stagedPhotos[`${job.id}-${stage.id}`].length})`}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => updateStage(job.id, stage.id, "completed")}
                                    disabled={updatingStage === `${job.id}-${stage.id}`}
                                    className="px-3 py-1 text-xs uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors cursor-pointer bg-transparent disabled:opacity-50"
                                  >
                                    {updatingStage === `${job.id}-${stage.id}` ? "..." : "Complete"}
                                  </button>
                                )}
                                {/* Skip button - always available */}
                                <button
                                  onClick={() => updateStage(job.id, stage.id, "skipped")}
                                  disabled={updatingStage === `${job.id}-${stage.id}`}
                                  className="px-3 py-1 text-xs uppercase font-body border border-zinc-700 text-zinc-600 hover:text-zinc-400 transition-colors cursor-pointer bg-transparent disabled:opacity-50"
                                >
                                  Skip
                                </button>
                              </div>
                            )}
                            {/* Add more photos button for completed stages */}
                            {canAddMore && (
                              <label className="px-2 py-1 text-xs uppercase font-body border border-[#3b82f6]/20 text-[#3b82f6]/60 hover:text-[#3b82f6] hover:border-[#3b82f6]/40 transition-colors cursor-pointer flex items-center gap-1">
                                <Plus size={10} /> Add More
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => addMorePhotos(job.id, stage.id, e.target.files, photos.length)}
                                />
                              </label>
                            )}
                          </div>
                          {/* Staged photos preview - before upload */}
                          {stagedPhotos[`${job.id}-${stage.id}`]?.length > 0 && (
                            <div className="mt-3 ml-7">
                              <p className="text-xs text-[#3b82f6] mb-2 font-body">Ready to upload ({stagedPhotos[`${job.id}-${stage.id}`].length}/10):</p>
                              <div className="flex gap-2 flex-wrap">
                                {stagedPhotos[`${job.id}-${stage.id}`].map((photo, idx) => (
                                  <div key={idx} className="relative group">
                                    <div className="w-20 h-20 border-2 border-dashed border-[#3b82f6]/50 overflow-hidden">
                                      <img src={photo.preview} alt={`Staged ${idx + 1}`} className="w-full h-full object-cover" />
                                    </div>
                                    <button
                                      onClick={() => removeStagedPhoto(job.id, stage.id, idx)}
                                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full cursor-pointer border-none flex items-center justify-center"
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                ))}
                                {stagedPhotos[`${job.id}-${stage.id}`].length < 10 && (
                                  <label className="w-20 h-20 border-2 border-dashed border-zinc-700 flex items-center justify-center cursor-pointer hover:border-[#3b82f6]/50 transition-colors">
                                    <Plus size={20} className="text-zinc-600" />
                                    <input
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      className="hidden"
                                      onChange={(e) => handlePhotoUpload(job.id, stage.id, e.target.files)}
                                    />
                                  </label>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Show photos if exist - with delete option */}
                          {hasPhotos && (
                            <div className="mt-3 ml-7 flex gap-2 flex-wrap">
                              {photos.map((photo, idx) => (
                                <div key={idx} className="relative group">
                                  <button
                                    onClick={() => { setLightboxPhotos(photos); setLightboxIndex(idx); }}
                                    className="w-20 h-20 border border-white/10 hover:border-[#3b82f6]/50 transition-colors cursor-pointer bg-transparent p-0 overflow-hidden"
                                  >
                                    <img src={photo} alt={`${stage.name} ${idx + 1}`} className="w-full h-full object-cover" />
                                  </button>
                                  <button
                                    onClick={() => deletePhoto(job.id, "stage", idx, stage.id)}
                                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer border-none flex items-center justify-center"
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Notes section with AI generation */}
                          <div className="mt-3 ml-7">
                            {editingNotes === `${job.id}-${stage.id}` ? (
                              <div className="space-y-2">
                                {/* AI Input area */}
                                <div className="flex gap-2 items-center text-xs text-zinc-500 bg-white/5 p-2 border border-white/10">
                                  <Sparkles size={12} className="text-[#3b82f6]" />
                                  <input
                                    type="text"
                                    value={aiVoiceInput}
                                    onChange={(e) => setAiVoiceInput(e.target.value)}
                                    placeholder="Describe work (or use mic) then click AI..."
                                    className="flex-1 bg-transparent border-none text-white font-body text-xs outline-none placeholder-zinc-600"
                                  />
                                  <button
                                    type="button"
                                    onClick={isRecordingVoice ? stopVoiceRecording : startVoiceRecording}
                                    className={`p-1.5 border transition-colors cursor-pointer ${
                                      isRecordingVoice 
                                        ? "bg-red-500/20 border-red-500/40 text-red-400 animate-pulse" 
                                        : "bg-transparent border-white/10 text-zinc-500 hover:text-white"
                                    }`}
                                    title={isRecordingVoice ? "Stop" : "Voice"}
                                  >
                                    {isRecordingVoice ? <StopCircle size={12} /> : <Mic size={12} />}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => generateAIDescription(job, stage.id, stage.name, getStagePhotos(stageData))}
                                    disabled={aiGenerating === `${job.id}-${stage.id}`}
                                    className="px-2 py-1 text-xs uppercase font-body bg-[#3b82f6]/20 text-[#3b82f6] border border-[#3b82f6]/30 cursor-pointer hover:bg-[#3b82f6]/30 transition-colors disabled:opacity-50"
                                    title="Generate customer description from photos + notes"
                                  >
                                    {aiGenerating === `${job.id}-${stage.id}` ? <Loader2 size={12} className="animate-spin" /> : "AI"}
                                  </button>
                                </div>
                                {/* Notes textarea */}
                                <div className="flex gap-2 items-end">
                                  <textarea
                                    data-notes-key={`${job.id}-${stage.id}`}
                                    value={notesText}
                                    onChange={(e) => {
                                      setNotesText(e.target.value);
                                      setPendingNotes(prev => ({ ...prev, [`${job.id}-${stage.id}`]: e.target.value }));
                                    }}
                                    placeholder="Customer-facing description (or click AI to auto-generate)..."
                                    className="flex-1 bg-white/5 border border-white/10 text-white font-body text-xs p-2 outline-none focus:border-[#3b82f6]/50 resize-none h-20"
                                  />
                                  <div className="flex flex-col gap-1">
                                    <VoiceButton
                                      onText={(t) => {
                                        const key = `${job.id}-${stage.id}`;
                                        setNotesText((prev) => (prev ? prev + " " : "") + t.trim());
                                        setPendingNotes((prev) => ({ ...prev, [key]: ((prev[key] || notesText || "") + " " + t.trim()).trim() }));
                                      }}
                                      size={14}
                                      testId={`notes-voice-btn-${job.id}-${stage.id}`}
                                      title="Dictate notes"
                                      className="px-2 py-1 border border-white/10 text-zinc-400"
                                    />
                                    <button onClick={() => saveNotes(job.id, stage.id)} className="px-2 py-1 text-xs uppercase font-body bg-[#3b82f6] text-white border-none cursor-pointer">Save</button>
                                    <button onClick={() => { setEditingNotes(null); setAiVoiceInput(""); setPendingNotes(prev => { const n = {...prev}; delete n[`${job.id}-${stage.id}`]; return n; }); }} className="px-2 py-1 text-xs uppercase font-body border border-white/10 text-zinc-500 cursor-pointer bg-transparent">Cancel</button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setEditingNotes(`${job.id}-${stage.id}`); setNotesText(stageData?.notes || pendingNotes[`${job.id}-${stage.id}`] || ""); }}
                                className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors cursor-pointer bg-transparent border-none flex items-center gap-1"
                              >
                                {stageData?.notes ? (
                                  <span className="text-zinc-500 italic">&ldquo;{stageData.notes}&rdquo;</span>
                                ) : pendingNotes[`${job.id}-${stage.id}`] ? (
                                  <span className="text-[#f59e0b] italic">&ldquo;{pendingNotes[`${job.id}-${stage.id}`]}&rdquo; (unsaved)</span>
                                ) : (
                                  <><Sparkles size={10} className="text-[#3b82f6]" /> Add AI description</>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Pre-Work / Post-Work Photos */}
                    <div className="border-t border-white/5 pt-4 mt-4">
                      <ConditionPhotos job={job} onUpdate={onUpdate} openLightbox={(photos, idx) => { setLightboxPhotos(photos); setLightboxIndex(idx); }} onDeletePhoto={deletePhoto} />
                    </div>
                    
                    {/* Price Tracking */}
                    <div className="border-t border-white/5 pt-4 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs tracking-[0.15em] uppercase text-zinc-500 font-body flex items-center gap-2">
                          <DollarSign size={12} /> Pricing
                        </span>
                        {editingPrices !== job.id && (
                          <button
                            onClick={() => startEditingPrices(job)}
                            className="text-xs text-zinc-600 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
                          >
                            <Edit2 size={12} />
                          </button>
                        )}
                      </div>
                      {editingPrices === job.id ? (
                        <div className="flex gap-3 items-end">
                          <div className="flex-1">
                            <label className="block text-xs text-zinc-600 mb-1">Quoted $</label>
                            <input
                              type="number"
                              value={priceForm.quoted}
                              onChange={(e) => setPriceForm(p => ({ ...p, quoted: e.target.value }))}
                              placeholder="0.00"
                              className="w-full bg-transparent border border-white/10 text-white font-mono text-sm py-2 px-3 outline-none focus:border-[#22c55e]/50"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-zinc-600 mb-1">Final $</label>
                            <input
                              type="number"
                              value={priceForm.final}
                              onChange={(e) => setPriceForm(p => ({ ...p, final: e.target.value }))}
                              placeholder="0.00"
                              className="w-full bg-transparent border border-white/10 text-white font-mono text-sm py-2 px-3 outline-none focus:border-[#22c55e]/50"
                            />
                          </div>
                          <button
                            onClick={() => savePrices(job.id)}
                            className="px-3 py-2 text-xs uppercase font-body bg-[#22c55e] text-white border-none hover:bg-[#22c55e]/80 transition-colors cursor-pointer"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingPrices(null)}
                            className="px-3 py-2 text-xs uppercase font-body border border-white/10 text-zinc-500 hover:text-white transition-colors cursor-pointer bg-transparent"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-6">
                          <div className="bg-white/5 px-4 py-2 flex-1 text-center">
                            <p className="text-xs text-zinc-600 mb-1">Quoted</p>
                            <p className="font-mono text-lg text-white">
                              {job.quoted_price ? `$${job.quoted_price.toLocaleString()}` : '—'}
                            </p>
                          </div>
                          <div className="bg-[#22c55e]/10 px-4 py-2 flex-1 text-center border border-[#22c55e]/20">
                            <p className="text-xs text-[#22c55e]/60 mb-1">Final</p>
                            <p className="font-mono text-lg text-[#22c55e]">
                              {job.final_price ? `$${job.final_price.toLocaleString()}` : '—'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Customer View Tracking */}
                    {(job.customer_views?.length > 0 || job.last_viewed_at) && (
                      <div className="mt-4 p-4 bg-[#22c55e]/5 border border-[#22c55e]/20">
                        <div className="flex items-center gap-2 mb-3">
                          <Eye size={14} className="text-[#22c55e]" />
                          <h5 className="text-xs tracking-[0.15em] uppercase text-[#22c55e]">Customer Views</h5>
                          <span className="ml-auto text-xs text-zinc-500 font-mono">
                            {job.customer_views?.length || 0} view{job.customer_views?.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {job.last_viewed_at && (
                          <p className="text-xs text-zinc-400 mb-2">
                            Last viewed: <span className="text-white font-mono">
                              {new Date(job.last_viewed_at).toLocaleString('en-AU', { 
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true 
                              })}
                            </span>
                          </p>
                        )}
                        {job.customer_views?.length > 0 && (
                          <div className="space-y-1 max-h-[100px] overflow-y-auto">
                            {job.customer_views.slice(-5).reverse().map((view) => (
                              <div key={view.viewed_at} className="flex items-center gap-2 text-xs">
                                <span className="text-zinc-600 font-mono">
                                  {new Date(view.viewed_at).toLocaleString('en-AU', { 
                                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true 
                                  })}
                                </span>
                                <span className="text-zinc-500">—</span>
                                <span className="text-zinc-400">Stage: {view.stage_at_view || 'unknown'}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex flex-wrap justify-end gap-2 pt-4">
                      {job.customer_email && (
                        <div className="flex gap-1 mr-auto flex-wrap">
                          <button 
                            onClick={() => resendEmail(job.id, "tracking")}
                            disabled={resendingEmail === `${job.id}-tracking`}
                            className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-body border border-white/10 text-zinc-500 hover:text-white hover:border-white/20 transition-colors cursor-pointer bg-transparent disabled:opacity-50"
                            title="Resend tracking code email"
                          >
                            {resendingEmail === `${job.id}-tracking` ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Track
                          </button>
                          <button 
                            onClick={() => resendEmail(job.id, "stage_update")}
                            disabled={resendingEmail === `${job.id}-stage_update`}
                            className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-body border border-white/10 text-zinc-500 hover:text-white hover:border-white/20 transition-colors cursor-pointer bg-transparent disabled:opacity-50"
                            title="Send stage update email"
                          >
                            {resendingEmail === `${job.id}-stage_update` ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Update
                          </button>
                          <button 
                            onClick={() => resendEmail(job.id, "aftercare")}
                            disabled={resendingEmail === `${job.id}-aftercare`}
                            className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-body border border-white/10 text-zinc-500 hover:text-white hover:border-white/20 transition-colors cursor-pointer bg-transparent disabled:opacity-50"
                            title="Send aftercare/paint care guide email"
                          >
                            {resendingEmail === `${job.id}-aftercare` ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Aftercare
                          </button>
                          {/* Google Review Request - only show for ready/completed jobs */}
                          {(job.current_stage === "ready" || job.status === "completed") && (
                            <button 
                              onClick={() => resendEmail(job.id, "review")}
                              disabled={resendingEmail === `${job.id}-review`}
                              className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-body border border-[#f59e0b]/40 text-[#f59e0b] hover:bg-[#f59e0b]/10 transition-colors cursor-pointer bg-transparent disabled:opacity-50"
                              title="Send Google Review request email"
                              data-testid={`send-review-${job.id}`}
                            >
                              {resendingEmail === `${job.id}-review` ? <Loader2 size={11} className="animate-spin" /> : <Star size={11} />} Review
                            </button>
                          )}
                          <button 
                            onClick={() => openAdminPreview(job.tracking_code)}
                            className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-body border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors cursor-pointer bg-transparent"
                            title="Preview tracker page (won't trigger customer viewed notification)"
                          >
                            <Eye size={11} /> Preview
                          </button>
                        </div>
                      )}
                      {/* PDF Invoice Button */}
                      <a 
                        href={`${API}/admin/jobs/${job.id}/invoice`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors cursor-pointer no-underline"
                        title="Download PDF Invoice"
                      >
                        <Download size={11} /> Invoice
                      </a>
                      {/* Xero Sync Button */}
                      <button 
                        onClick={() => syncToXero(job.id)}
                        disabled={xeroSyncing === job.id}
                        className={`flex items-center gap-1 px-2 py-1 text-xs uppercase font-body border transition-colors cursor-pointer bg-transparent ${
                          job.xero_invoice_id 
                            ? 'border-[#13B5EA]/30 text-[#13B5EA]/60' 
                            : 'border-[#13B5EA]/30 text-[#13B5EA] hover:bg-[#13B5EA]/10'
                        }`}
                        title={job.xero_invoice_id ? `Synced: ${job.xero_invoice_number}` : "Send to Xero"}
                      >
                        {xeroSyncing === job.id ? <Loader2 size={11} className="animate-spin" /> : <DollarSign size={11} />} 
                        {job.xero_invoice_id ? 'Synced' : 'Xero'}
                      </button>
                      {/* Customer History Button */}
                      {job.customer_email && (
                        <button 
                          onClick={() => window.open(`/dashboard?tab=customer&id=${encodeURIComponent(job.customer_email)}`, '_blank')}
                          className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-body border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors cursor-pointer bg-transparent"
                          title="View customer history"
                        >
                          <User size={11} /> History
                        </button>
                      )}
                      <button onClick={() => deleteJob(job.id, job.vehicle)}
                        className="flex items-center gap-1 px-3 py-1 text-xs uppercase font-body border border-red-500/20 text-red-500/50 hover:text-red-400 hover:border-red-500/40 transition-colors cursor-pointer bg-transparent">
                        <Trash2 size={11} /> Delete Job
                      </button>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completed Jobs Archive */}
      {completedJobs.length > 0 && (
        <CompletedJobsArchive jobs={completedJobs} stages={jobs.stages} />
      )}

      {/* Empty State */}
      {jobs.jobs?.length === 0 && (
        <div className="border p-12 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
          <Truck size={32} className="mx-auto mb-4" style={{ color: 'var(--dash-text-dim)' }} />
          <p className="font-body" style={{ color: 'var(--dash-text-muted)' }}>No jobs yet. Click &ldquo;New Job&rdquo; to start tracking a vehicle.</p>
        </div>
      )}

      {/* Clock Out Notes Modal */}
      {clockOutModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" onClick={() => setClockOutModal(null)} data-testid="clock-out-modal">
          <div className="border w-full max-w-md p-6" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }} onClick={e => e.stopPropagation()}>
            <h3 className="font-heading text-lg uppercase tracking-wider mb-1" style={{ color: 'var(--dash-text)' }}>Clock Out</h3>
            <p className="text-sm font-body mb-4" style={{ color: 'var(--dash-text-muted)' }}>What did you work on?</p>
            <textarea
              value={clockOutNotes}
              onChange={e => setClockOutNotes(e.target.value)}
              placeholder="e.g. Sanded front bumper, applied primer coat..."
              className="w-full border text-sm p-3 h-24 resize-none focus:outline-none focus:border-[#3b82f6]/50 font-body"
              style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
              autoFocus
              data-testid="clock-out-notes-input"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={submitClockOut}
                disabled={timerLoading}
                className="flex-1 bg-[#3b82f6] text-white font-heading text-sm tracking-[0.1em] uppercase py-2.5 hover:bg-[#3b82f6]/80 transition-colors cursor-pointer border-none"
                data-testid="clock-out-submit"
              >
                {timerLoading ? "Saving..." : "Clock Out"}
              </button>
              <button
                onClick={() => setClockOutModal(null)}
                className="px-4 py-2.5 bg-white/5 border border-white/10 text-zinc-400 hover:text-white font-heading text-sm tracking-[0.1em] uppercase transition-colors cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timer Management Modal */}
      {timerModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" onClick={closeTimerModal} data-testid="timer-modal">
          <div className="border w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--dash-border)' }}>
              <div>
                <h3 className="font-heading text-lg uppercase tracking-wider" style={{ color: 'var(--dash-text)' }}>
                  <Timer size={18} className="inline mr-2" />
                  Timer History
                </h3>
                <p className="text-sm font-body mt-1" style={{ color: 'var(--dash-text-muted)' }}>
                  {timerModal.jobVehicle} • {timerModal.jobCustomer}
                </p>
              </div>
              <button onClick={closeTimerModal} className="text-zinc-500 hover:text-white bg-transparent border-none cursor-pointer">
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Add Manual Timer Button/Form */}
              {!addingManualTimer ? (
                <button
                  onClick={() => setAddingManualTimer(true)}
                  className="w-full py-3 border-2 border-dashed border-white/10 text-zinc-500 hover:border-[#3b82f6]/50 hover:text-[#3b82f6] transition-colors cursor-pointer bg-transparent flex items-center justify-center gap-2"
                  data-testid="add-manual-timer-btn"
                >
                  <Plus size={16} /> Add Manual Time Entry
                </button>
              ) : (
                <div className="p-4 border" style={{ backgroundColor: 'var(--dash-bg)', borderColor: 'var(--dash-border)' }}>
                  <h4 className="text-sm font-heading uppercase tracking-wider mb-4" style={{ color: 'var(--dash-text)' }}>Add Manual Entry</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Team Member *</label>
                      <select
                        value={manualTimerForm.user}
                        onChange={(e) => setManualTimerForm(f => ({ ...f, user: e.target.value }))}
                        className="w-full border text-sm p-2 focus:outline-none focus:border-[#3b82f6]/50"
                        style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                        data-testid="manual-timer-user"
                      >
                        <option value="">Select...</option>
                        <option value="VPPBlake">Blake</option>
                        <option value="VPPHarrisson">Harrisson</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Date *</label>
                      <input
                        type="date"
                        value={manualTimerForm.date}
                        onChange={(e) => setManualTimerForm(f => ({ ...f, date: e.target.value }))}
                        className="w-full border text-sm p-2 focus:outline-none focus:border-[#3b82f6]/50"
                        style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                        data-testid="manual-timer-date"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Start Time *</label>
                      <input
                        type="time"
                        value={manualTimerForm.start_time}
                        onChange={(e) => setManualTimerForm(f => ({ ...f, start_time: e.target.value }))}
                        className="w-full border text-sm p-2 focus:outline-none focus:border-[#3b82f6]/50"
                        style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                        data-testid="manual-timer-start"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">End Time *</label>
                      <input
                        type="time"
                        value={manualTimerForm.end_time}
                        onChange={(e) => setManualTimerForm(f => ({ ...f, end_time: e.target.value }))}
                        className="w-full border text-sm p-2 focus:outline-none focus:border-[#3b82f6]/50"
                        style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                        data-testid="manual-timer-end"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs text-zinc-500 mb-1">Notes</label>
                    <textarea
                      value={manualTimerForm.notes}
                      onChange={(e) => setManualTimerForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="What was worked on..."
                      className="w-full border text-sm p-2 h-16 resize-none focus:outline-none focus:border-[#3b82f6]/50"
                      style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                      data-testid="manual-timer-notes"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={addManualTimer}
                      disabled={timerLoading === "manual-add"}
                      className="flex-1 bg-[#3b82f6] text-white font-heading text-xs tracking-[0.1em] uppercase py-2 hover:bg-[#3b82f6]/80 transition-colors cursor-pointer border-none disabled:opacity-50"
                      data-testid="manual-timer-submit"
                    >
                      {timerLoading === "manual-add" ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Add Entry"}
                    </button>
                    <button
                      onClick={() => { setAddingManualTimer(false); setManualTimerForm({ user: "", date: "", start_time: "", end_time: "", notes: "" }); }}
                      className="px-4 py-2 bg-white/5 border border-white/10 text-zinc-400 hover:text-white font-heading text-xs tracking-[0.1em] uppercase transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Timer History List */}
              {timerHistoryLoading ? (
                <div className="py-8 text-center">
                  <Loader2 size={24} className="animate-spin mx-auto text-[#3b82f6]" />
                  <p className="text-sm text-zinc-500 mt-2">Loading timer history...</p>
                </div>
              ) : jobTimerHistory.length === 0 ? (
                <div className="py-8 text-center border border-dashed" style={{ borderColor: 'var(--dash-border)' }}>
                  <Clock size={32} className="mx-auto mb-2 text-zinc-600" />
                  <p className="text-sm" style={{ color: 'var(--dash-text-muted)' }}>No time entries yet</p>
                  <p className="text-xs text-zinc-600 mt-1">Clock in to start tracking, or add a manual entry above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs uppercase tracking-wider text-zinc-500">All Entries ({jobTimerHistory.length})</h4>
                    <span className="text-sm font-mono text-[#3b82f6]">
                      Total: {Math.floor(jobTimerHistory.reduce((sum, t) => sum + (t.duration_minutes || 0), 0) / 60)}h {Math.round(jobTimerHistory.reduce((sum, t) => sum + (t.duration_minutes || 0), 0) % 60)}m
                    </span>
                  </div>
                  {jobTimerHistory.map((timer) => (
                    <div 
                      key={timer.id} 
                      className={`p-3 border ${timer.auto_stopped ? 'border-amber-500/30 bg-amber-500/5' : ''} ${timer.manual_entry ? 'border-[#3b82f6]/30 bg-[#3b82f6]/5' : ''}`}
                      style={{ backgroundColor: timer.auto_stopped || timer.manual_entry ? undefined : 'var(--dash-bg)', borderColor: timer.auto_stopped || timer.manual_entry ? undefined : 'var(--dash-border)' }}
                    >
                      {editingTimerId === timer.id ? (
                        // Edit form
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-zinc-500 mb-1">Clock In</label>
                              <input
                                type="datetime-local"
                                value={editTimerForm.clock_in}
                                onChange={(e) => setEditTimerForm(f => ({ ...f, clock_in: e.target.value }))}
                                className="w-full border text-sm p-2 focus:outline-none focus:border-[#3b82f6]/50"
                                style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-zinc-500 mb-1">Clock Out</label>
                              <input
                                type="datetime-local"
                                value={editTimerForm.clock_out}
                                onChange={(e) => setEditTimerForm(f => ({ ...f, clock_out: e.target.value }))}
                                className="w-full border text-sm p-2 focus:outline-none focus:border-[#3b82f6]/50"
                                style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-zinc-500 mb-1">Notes</label>
                            <input
                              type="text"
                              value={editTimerForm.notes}
                              onChange={(e) => setEditTimerForm(f => ({ ...f, notes: e.target.value }))}
                              className="w-full border text-sm p-2 focus:outline-none focus:border-[#3b82f6]/50"
                              style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveEditTimer(timer.id)}
                              disabled={timerLoading === `edit-${timer.id}`}
                              className="px-3 py-1 bg-[#22c55e] text-white text-xs uppercase font-body hover:bg-[#22c55e]/80 transition-colors cursor-pointer border-none disabled:opacity-50"
                            >
                              {timerLoading === `edit-${timer.id}` ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingTimerId(null)}
                              className="px-3 py-1 bg-white/5 border border-white/10 text-zinc-400 hover:text-white text-xs uppercase font-body transition-colors cursor-pointer"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Display mode
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-body text-sm" style={{ color: 'var(--dash-text)' }}>
                                {timer.user?.replace('VPP', '') || 'Unknown'}
                              </span>
                              {timer.manual_entry && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-[#3b82f6]/20 text-[#3b82f6] uppercase tracking-wider">Manual</span>
                              )}
                              {timer.auto_stopped && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 uppercase tracking-wider">Auto-stopped</span>
                              )}
                              {!timer.clock_out && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-[#22c55e]/20 text-[#22c55e] uppercase tracking-wider animate-pulse">Active</span>
                              )}
                            </div>
                            <div className="text-xs text-zinc-500 font-mono">
                              {new Date(timer.clock_in).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                              {' • '}
                              {new Date(timer.clock_in).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                              {timer.clock_out && (
                                <> → {new Date(timer.clock_out).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</>
                              )}
                            </div>
                            {timer.notes && (
                              <p className="text-xs mt-1 italic" style={{ color: 'var(--dash-text-muted)' }}>&ldquo;{timer.notes}&rdquo;</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {timer.duration_minutes > 0 && (
                              <span className="font-mono text-sm font-semibold" style={{ color: 'var(--dash-text)' }}>
                                {timer.duration_minutes >= 60 
                                  ? `${Math.floor(timer.duration_minutes / 60)}h ${Math.round(timer.duration_minutes % 60)}m`
                                  : `${Math.round(timer.duration_minutes)}m`
                                }
                              </span>
                            )}
                            <div className="flex gap-1">
                              <button
                                onClick={() => startEditTimer(timer)}
                                className="p-1.5 text-zinc-600 hover:text-[#3b82f6] bg-transparent border-none cursor-pointer"
                                title="Edit timer"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => deleteTimer(timer.id)}
                                disabled={timerLoading === `delete-${timer.id}`}
                                className="p-1.5 text-zinc-600 hover:text-red-400 bg-transparent border-none cursor-pointer disabled:opacity-50"
                                title="Delete timer"
                              >
                                {timerLoading === `delete-${timer.id}` ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex justify-end" style={{ borderColor: 'var(--dash-border)' }}>
              <button
                onClick={closeTimerModal}
                className="px-4 py-2 bg-white/5 border border-white/10 text-zinc-400 hover:text-white font-heading text-sm tracking-[0.1em] uppercase transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export { JobTrackerAdmin };
